import type { SajuAnalysis } from '../types/input';
import type { FortuneFlowCompat, FortuneFlowEntry, FortuneFlowMatch, YearlyFortuneFlowEntry, FortuneTimingEntry } from '../types/compatibility';
import { getStemElement, getBranchElement, getElementRelationship } from '../core/five-elements';
import { getYearlyFortune } from '../fortune/yearly-fortune';

/**
 * 두 사주의 대운 흐름을 비교한다.
 *
 * P1의 대운 각 10년 구간에 대해, 해당 시기에 P2가 어떤 대운에 있는지 매칭한다.
 * 하나의 P1 대운 구간에 P2 대운이 2개 걸릴 수 있으며, 이 경우 비율로 표시한다.
 */
export function analyzeFortuneFlow(
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
  referenceYear?: number,
): FortuneFlowCompat {
  const entries1 = analysis1.majorFortune.entries;
  const entries2 = analysis2.majorFortune.entries;

  const entries: FortuneFlowEntry[] = [];

  for (const e1 of entries1) {
    const matches: FortuneFlowMatch[] = [];

    for (const e2 of entries2) {
      const startYear = Math.max(e1.startYear, e2.startYear);
      const endYear = Math.min(e1.endYear, e2.endYear);

      if (startYear < endYear) {
        const elem1 = getStemElement(e1.pillar.stem);
        const elem2 = getStemElement(e2.pillar.stem);
        const relation = getElementRelationship(elem1, elem2);

        const brElem1 = getBranchElement(e1.pillar.branch);
        const brElem2 = getBranchElement(e2.pillar.branch);
        const branchRelation = getElementRelationship(brElem1, brElem2);

        matches.push({
          person2Pillar: e2.pillar,
          startYear,
          endYear,
          years: endYear - startYear,
          relation,
          branchRelation,
        });
      }
    }

    if (matches.length > 0) {
      entries.push({
        person1Pillar: e1.pillar,
        startYear: e1.startYear,
        endYear: e1.endYear,
        matches,
      });
    }
  }

  // 시너지: 천간(40%) + 지지(60%) 가중 평균
  const isSynergy = (r: string) => r === 'same' || r === 'generates' || r === 'generated';
  let stemSynergy = 0;
  let branchSynergy = 0;
  let totalYears = 0;
  for (const entry of entries) {
    for (const m of entry.matches) {
      totalYears += m.years;
      if (isSynergy(m.relation)) stemSynergy += m.years;
      if (isSynergy(m.branchRelation)) branchSynergy += m.years;
    }
  }
  const synergyYears = stemSynergy * 0.4 + branchSynergy * 0.6;

  const synergyScore = totalYears > 0 ? Math.round((synergyYears / totalYears) * 100) / 100 : 0.5;

  let label: string;
  if (synergyScore >= 0.7) label = '매우 좋음';
  else if (synergyScore >= 0.5) label = '좋음';
  else if (synergyScore >= 0.3) label = '보통';
  else label = '주의';

  // 세운 교차: 기준연도 ±1 (3년)
  const currentYear = referenceYear ?? new Date().getFullYear();
  const yearlyEntries: YearlyFortuneFlowEntry[] = [];
  const isSynergyRel = (r: string) => r === 'same' || r === 'generates' || r === 'generated';
  let yearlyStemSyn = 0;
  let yearlyBranchSyn = 0;

  for (let y = currentYear - 1; y <= currentYear + 1; y++) {
    const yf1 = getYearlyFortune(analysis1.dayMaster, y);
    const yf2 = getYearlyFortune(analysis2.dayMaster, y);

    const stemRel = getElementRelationship(
      getStemElement(yf1.pillar.stem), getStemElement(yf2.pillar.stem),
    );
    const branchRel = getElementRelationship(
      getBranchElement(yf1.pillar.branch), getBranchElement(yf2.pillar.branch),
    );

    yearlyEntries.push({
      year: y,
      person1Pillar: yf1.pillar,
      person2Pillar: yf2.pillar,
      stemRelation: stemRel,
      branchRelation: branchRel,
    });

    if (isSynergyRel(stemRel)) yearlyStemSyn++;
    if (isSynergyRel(branchRel)) yearlyBranchSyn++;
  }

  const yearlyTotal = yearlyEntries.length;
  const yearlySynergyScore = yearlyTotal > 0
    ? Math.round(((yearlyStemSyn * 0.4 + yearlyBranchSyn * 0.6) / yearlyTotal) * 100) / 100
    : 0.5;

  // 시기 서술: 대운 구간별 유리/불리 판정
  const timingNarrative: FortuneTimingEntry[] = [];
  for (const entry of entries) {
    let stemSyn = 0;
    let branchSyn = 0;
    let years = 0;
    for (const m of entry.matches) {
      years += m.years;
      if (isSynergy(m.relation)) stemSyn += m.years;
      if (isSynergy(m.branchRelation)) branchSyn += m.years;
    }
    const entrySynergy = years > 0 ? (stemSyn * 0.4 + branchSyn * 0.6) / years : 0.5;
    let assessment: FortuneTimingEntry['assessment'];

    // 구체적 대운 관계를 reason에 포함
    const RELATION_KO: Record<string, string> = {
      same: '비화', generates: '상생', generated: '피생', overcomes: '상극', overcome: '피극',
    };
    const matchDesc = entry.matches.map(m =>
      `P1(${entry.person1Pillar.ganji})↔P2(${m.person2Pillar.ganji}): 천간 ${RELATION_KO[m.relation] ?? m.relation}, 지지 ${RELATION_KO[m.branchRelation] ?? m.branchRelation}`,
    ).join('; ');

    let reason: string;
    if (entrySynergy >= 0.6) {
      assessment = '유리';
      reason = `상생/비화 우세 — ${matchDesc}`;
    } else if (entrySynergy >= 0.3) {
      assessment = '보통';
      reason = `관계 혼재 — ${matchDesc}`;
    } else {
      assessment = '주의';
      reason = `상극 우세 — ${matchDesc}`;
    }
    timingNarrative.push({
      period: `${entry.startYear}~${entry.endYear}`,
      assessment,
      reason,
    });
  }

  const description = `대운 흐름: ${entries.length}구간, 시너지 ${Math.round(synergyScore * 100)}% — ${label}` +
    ` | 세운(${currentYear - 1}~${currentYear + 1}): ${Math.round(yearlySynergyScore * 100)}%`;

  return { entries, synergyScore, yearlyEntries, yearlySynergyScore, timingNarrative, description };
}
